import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { MojoWebsocketService } from 'mojo-websocket';

import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { getNotificationsAll } from '@collections/notifications/notifications.selectors';
import { getUsersLoggedIn, hasAnonymization } from '@collections/users';
import { isWebSocketConnected } from '@core/websocket';
import { NOTIFICATION_LICENSES } from '@modules/notifications/notifications.module';
import { Notification, NotificationType, User } from '@shared/models';
import { NotificationsService } from '@shared/services/notifications.service';
import { createAnonymizedName, isTruthy } from '@shared/utils';

import { NotificationLicenseType } from './notification.config';
import { getNotificationTypes } from './notification.utils';
import * as NotificationsActions from './notifications.actions';
import { notificationsAddNew } from './notifications.actions';

const NOTIFICATION_CREATED_EVENT = 'notificationCreated';

@Injectable()
export class NotificationsEffects {
  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.notificationsLoadPage),
      withLatestFrom(
        this.store.pipe(select(getUsersLoggedIn)),
        this.store.select(hasAnonymization),
      ),
      mergeMap(([{ apiParams }, loggedInUser, isAnonymized]) => {
        const type = getNotificationTypes(this.notificationLicenses, loggedInUser.resources);

        return this.notificationsService.getPage(apiParams, type).pipe(
          map((page) => {
            page.content = this.updateContentAnonymized(isAnonymized, page.content);
            return NotificationsActions.notificationsLoadPageSuccess({ page });
          }),
          catchError((error) => of(NotificationsActions.notificationsLoadPageFail({ error }))),
        );
      }),
    ),
  );

  updateNotificationsAnonymization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.notificationsUpdateAnonymizedNames),
      withLatestFrom(this.store.pipe(select(getNotificationsAll))),
      map(([{ isAnonymized }, notifications]) => {
        notifications = this.updateContentAnonymized(isAnonymized, notifications);
        return NotificationsActions.notificationsUpdateAnonymizedNamesSuccess({ notifications });
      }),
    ),
  );

  liveNotifications$ = createEffect(() =>
    this.store.select(isWebSocketConnected).pipe(
      filter(isTruthy),
      mergeMap(() => this.mojoWebsocketService.subscribe('notifications')),
      switchMap(() => this.store.select(getUsersLoggedIn).pipe(take(1))),
      switchMap(
        (loggedInUser: User) =>
          new Observable<Action>((subscriber) => {
            this.mojoWebsocketService.registerCallback(
              NOTIFICATION_CREATED_EVENT,
              (notificationJson: string) => {
                const notification = JSON.parse(notificationJson) as Notification;
                if (loggedInUser.resources.includes(this.notificationLicenses[notification.type])) {
                  notification.isRead = false;
                  subscriber.next(notificationsAddNew({ notification }));
                }
                return () => subscriber.complete();
              },
            );
          }),
      ),
    ),
  );

  markAsReadAndRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.notificationsMarkAsRead),
      switchMap(() =>
        this.notificationsService
          .markAsRead()
          .pipe(
            mergeMap(() => [
              NotificationsActions.notificationsClear(),
              NotificationsActions.notificationsLoadPage({}),
            ]),
          ),
      ),
    ),
  );

  getNotificationsCount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        NotificationsActions.notificationsLoadPageSuccess,
        NotificationsActions.notificationsGetNewNotificationsCount,
      ),
      withLatestFrom(this.store.pipe(select(getUsersLoggedIn))),
      switchMap(([_, loggedInUser]) => {
        const type = getNotificationTypes(this.notificationLicenses, loggedInUser.resources);
        return this.notificationsService.getUnreadNotificationsCount(type).pipe(
          map((count) =>
            NotificationsActions.notificationsGetNewNotificationsCountSuccess({ count }),
          ),
          catchError((error) =>
            of(NotificationsActions.notificationsGetNewNotificationsCountFail({ error })),
          ),
        );
      }),
    ),
  );

  hideNotificationAndRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationsActions.hideNotification),
      switchMap(({ notificationId }) => {
        const refreshNotifications = [
          NotificationsActions.notificationsClear(),
          NotificationsActions.notificationsLoadPage({}),
        ];
        return this.notificationsService
          .hideNotification(notificationId)
          .pipe(mergeMap(() => refreshNotifications));
      }),
    ),
  );

  private updateContentAnonymized(
    isAnonymized: boolean,
    contentArray: Notification[],
  ): Notification[] {
    const resultArr = [];
    if (!!contentArray) {
      contentArray.forEach((notification, index) => {
        resultArr.push({ ...notification });
        if (
          !!notification &&
          !!notification.data &&
          (notification.type === NotificationType.INVITED_APPLIED ||
            notification.type === NotificationType.REFERRAL ||
            notification.type === NotificationType.DUPLICATE)
        ) {
          resultArr[index].data.displayAnonymized = isAnonymized;
          if (notification.type === NotificationType.DUPLICATE) {
            resultArr[index].data.anonymizedName = createAnonymizedName(
              resultArr[index].itemId,
              resultArr[index].data.initials,
            );
          }
        }
      });
    }
    return resultArr;
  }

  constructor(
    private actions$: Actions,
    private notificationsService: NotificationsService,
    private store: Store<{}>,
    private mojoWebsocketService: MojoWebsocketService,
    @Inject(NOTIFICATION_LICENSES) private notificationLicenses: NotificationLicenseType,
  ) {}
}
